import { Chip } from "@mui/material";
import type { AlertSeverity, AlertStatus, AlertType } from "../types";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

interface SeverityBadgeProps {
  severity: AlertSeverity;
  size?: "small" | "medium";
}

const severityConfig: Record<AlertSeverity, { label: string; color: "error" | "warning" | "success" | "default" }> = {
  critical: { label: "Critical", color: "error" },
  high: { label: "High", color: "warning" },
  medium: { label: "Medium", color: "warning" },
  low: { label: "Low", color: "success" },
};

export const SeverityBadge = ({ severity, size = "small" }: SeverityBadgeProps) => {
  const config = severityConfig[severity];
  return <Chip label={config.label} size={size} color={config.color} />;
};

interface StatusBadgeProps {
  status: AlertStatus;
  size?: "small" | "medium";
}

const statusConfig: Record<AlertStatus, { label: string; color: "error" | "info" | "warning" | "success" | "default"; icon: React.ReactElement }> = {
  active: { label: "Active", color: "error", icon: <WarningIcon /> },
  acknowledged: { label: "Acknowledged", color: "info", icon: <VisibilityIcon /> },
  investigating: { label: "Investigating", color: "warning", icon: <SearchIcon /> },
  resolved: { label: "Resolved", color: "success", icon: <CheckCircleIcon /> },
  closed: { label: "Closed", color: "default", icon: <CancelIcon /> },
};

export const StatusBadge = ({ status, size = "small" }: StatusBadgeProps) => {
  const config = statusConfig[status];
  return <Chip icon={config.icon} label={config.label} size={size} color={config.color} variant="outlined" />;
};

interface TypeBadgeProps {
  type: AlertType;
  size?: "small" | "medium";
}

const typeConfig: Record<AlertType, { label: string; color: "error" | "warning" | "secondary" | "info" | "success" }> = {
  leak: { label: "Leak", color: "error" },
  pressure: { label: "Pressure", color: "warning" },
  intrusion: { label: "Intrusion", color: "secondary" },
  equipment: { label: "Equipment", color: "info" },
  environmental: { label: "Environmental", color: "success" },
};

export const TypeBadge = ({ type, size = "small" }: TypeBadgeProps) => {
  const config = typeConfig[type];
  return <Chip label={config.label} size={size} color={config.color} variant="outlined" />;
};
